import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { thumbUrl, photoUrl, formatApiError } from "@/lib/utils-app";
import { PASSPORT_LABELS } from "@/pages/CarePassport";
import { PawPrintIcon as PawPrint, FootprintsIcon as Footprints, HouseIcon as House, ScissorsIcon as Scissors, GraduationCapIcon as GraduationCap, WarningIcon as Warning, DogIcon as Dog, SyringeIcon as Syringe, FileTextIcon as FileText } from "@phosphor-icons/react";
import { toast } from "sonner";

const SECTION_META = [
  ["core", "The Basics", PawPrint, "#B8860B"],
  ["walking", "Walking", Footprints, "#0F8043"],
  ["sitting", "Sitting & Boarding", House, "#C1272D"],
  ["grooming", "Grooming", Scissors, "#8A5CB8"],
  ["training", "Training & Behavior", GraduationCap, "#1F5FBF"],
  ["vaccinations", "Vaccinations (self-reported)", Syringe, "#2E6B3E"],
];

function fmt(v) {
  if (v === true) return "Yes";
  if (v === false) return "No";
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
}

/** Booked pro's view of a dog's Care Passport — the section for their
 * service kind floats to the top; safety flags pinned above everything. */
export default function PassportViewDialog({ open, onClose, dogId, booking, providerKind }) {
  const [p, setP] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && dogId) {
      setP(null);
      setNote("");
      api.get(`/passports/${dogId}`).then((r) => setP(r.data)).catch(() => setP(false));
    }
  }, [open, dogId]);

  const requestInfo = async () => {
    setBusy(true);
    try {
      await api.post(`/passports/${dogId}/request-info`, { booking_id: booking.id });
      toast.success("Owner notified — they'll get a push asking them to fill it in");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const suggest = async () => {
    if (note.trim().length < 3) return;
    setBusy(true);
    try {
      await api.post(`/passports/${dogId}/suggest`, { booking_id: booking.id, note: note.trim() });
      toast.success("Sent — the owner can approve it into the passport");
      setNote("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const kindSection = { walker: "walking", sitter: "sitting", groomer: "grooming", trainer: "training", field_host: "vaccinations" }[providerKind];
  const ordered = kindSection
    ? [...SECTION_META].sort((a, b) => (a[0] === kindSection ? -1 : b[0] === kindSection ? 1 : 0))
    : SECTION_META;

  const flags = [];
  if (p && typeof p === "object") {
    if (p.walking?.triggers?.length) flags.push(`Reactive to: ${p.walking.triggers.join(", ")}`);
    const meds = (p.core?.meds || "").trim();
    if (meds && !["none", "no", "n/a"].includes(meds.toLowerCase())) flags.push(`On medication: ${meds}`);
    if (p.grooming?.bite_history === true) flags.push("Bite history — handle with care");
    if (p.grooming?.muzzle_needed === true) flags.push("Muzzle needed");
    const bites = (p.training?.bite_incident_history || "").trim().replace(/[.!]+$/, "");
    if (bites && !["none", "no", "n/a"].includes(bites.toLowerCase())) flags.push(`Bite / incident history: ${bites}`);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="passport-view-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral flex items-center gap-2">
            {p?.dog?.photo ? (
              <img src={thumbUrl(p.dog.photo) || p.dog.photo} alt="" className="w-8 h-8 rounded-full object-cover border border-gold" />
            ) : <Dog size={20} weight="duotone" className="text-gold" />}
            {p?.dog?.name || "Care Passport"}
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            {p?.dog?.breed}{p?.completion ? ` · passport ${p.completion.overall}% complete` : ""}
          </DialogDescription>
        </DialogHeader>

        {p === null && <p className="text-sm text-[#6B6B6B] py-6 text-center">Loading…</p>}
        {p === false && <p className="text-sm text-crimson py-6 text-center">Couldn't load this passport.</p>}
        {p && typeof p === "object" && (
          <div className="space-y-4">
            {flags.length > 0 && (
              <div className="rounded-lg border border-[#C1272D]/40 bg-[#FDECEC]/70 p-3 space-y-1" data-testid="passport-flags">
                {flags.map((f) => (
                  <p key={f} className="text-[12.5px] font-semibold text-[#8f1d22] flex items-center gap-1.5">
                    <Warning size={13} weight="fill" /> {f}
                  </p>
                ))}
              </div>
            )}
            {(p.completion?.overall ?? 0) < 70 && booking && (
              <div className="rounded-lg border border-gold/40 bg-cream/60 p-3 flex items-center justify-between gap-3">
                <p className="text-[12px] text-ink">This passport is thin — ask the owner to fill it in before your visit.</p>
                <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={requestInfo} disabled={busy} data-testid="passport-request-info">
                  Ask owner
                </Button>
              </div>
            )}
            {ordered.map(([key, title, Icon, color]) => {
              const data = p[key] || {};
              const docs = key === "vaccinations" && Array.isArray(data.documents) ? data.documents : [];
              const rows = Object.entries(data).filter(([k, v]) => k !== "documents" && v !== null && v !== "" && !(Array.isArray(v) && !v.length));
              if (!rows.length && !docs.length) return null;
              return (
                <div key={key} className={`rounded-lg border p-3 ${key === kindSection ? "border-gold/50 bg-cream/40" : "border-[#EAEAEA]"}`}>
                  <p className="text-[11px] uppercase tracking-wider font-bold mb-2 flex items-center gap-1.5" style={{ color }}>
                    <Icon size={13} weight="duotone" /> {title}{key === kindSection ? " · your service" : ""}
                  </p>
                  <dl className="space-y-1.5">
                    {rows.map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-4 text-[12.5px]">
                        <dt className="text-[#6B6B6B] shrink-0">{PASSPORT_LABELS[k] || k}</dt>
                        <dd className="text-ink text-right font-medium">{fmt(v)}</dd>
                      </div>
                    ))}
                  </dl>
                  {docs.length > 0 && (
                    <div className="mt-2 space-y-1" data-testid="passport-vax-docs">
                      {docs.map((doc, i) => (
                        <a key={doc.storage_path} href={photoUrl(doc.storage_path)} target="_blank" rel="noreferrer"
                          className="flex items-center gap-1.5 text-[12px] font-semibold text-[#2E6B3E] hover:underline"
                          data-testid={`passport-vax-doc-${i}`}>
                          <FileText size={13} weight="duotone" /> {doc.name || `Record ${i + 1}`}
                        </a>
                      ))}
                      <p className="text-[10.5px] text-[#6B6B6B]">Uploaded by the owner — not verified by Armstrong K9 Registry.</p>
                    </div>
                  )}
                </div>
              );
            })}
            {booking?.status === "completed" && (
              <div className="rounded-lg border border-[#EAEAEA] p-3">
                <p className="text-[11px] uppercase tracking-wider font-bold text-[#6B6B6B] mb-1.5">Suggest a passport note</p>
                <Textarea rows={2} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder='e.g. "Ate better with the slow feeder" — the owner approves it in one tap'
                  data-testid="passport-suggest-note" />
                <Button size="sm" className="rounded-full mt-2 bg-ink hover:bg-crimson text-white" onClick={suggest}
                  disabled={busy || note.trim().length < 3} data-testid="passport-suggest-submit">
                  Send suggestion
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
